import React from "react";
import { Helmet } from "react-helmet-async";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import loadable from "@loadable/component";
import { imgixSrc } from "core-web/libs/image";
import decodeHTMLEntities from "core-web/utils/decodeHTMLEntities";
import getCountrySpecificVariable from "core-web/utils/getCountrySpecificVariable";
import getQueryParams from "core-web/utils/getQueryParams";
import { getHreflangs, getLanguages } from "core-web/utils/multimarket";
import LanguageLinks from "core-web/components/Meta/LanguageLinks";
import activeSetting from "theme/libs/wordpress/utils/activeSetting";

const structuredDatas = {
    webpage: loadable(() => import("core-web/components/Meta/StructuredData/WebPageStructuredData")),
    website: loadable(() => import("core-web/components/Meta/StructuredData/WebsiteStructuredData")),
};

const returnRobotString = (arr) =>
    arr.length
        ? arr
              .filter((x) => x)
              .join(", ")
              .trim()
        : null;

const isPresent = (prop) => {
    return prop && prop !== "" && prop !== "0" && prop.length > 0;
};

const buildCanonicalUrl = (page, application, query, isPaginationPage) => {
    const {
        data: {
            seo: { canonical },
            permalink,
        },
    } = page;
    const { origin, protocol } = application;
    const baseUrl = (protocol || "") + (origin || "");

    let url = baseUrl + permalink.toLowerCase();

    if (canonical) {
        url = baseUrl + canonical.toLowerCase();
    }

    const canonicalQuery = `?${Object.entries(query)
        .filter((q) => (q[0] === "page" && q[1] > 1) || q[0] === "category")
        .map((q) => q.join("="))
        .join("&")}`;

    if (isPaginationPage && canonicalQuery?.length > 1) {
        url += canonicalQuery;
    }

    return url;
};

const subDesc = (string) => {
    return string.length >= 160
        ? string
              .replace(/<\/{0,1}[^>]+>/g, "")
              .slice(0, 157)
              .concat("...")
        : string.replace(/<\/{0,1}[^>]+>/g, "");
};

const PageMeta = () => {
    const { t } = useTranslation();
    const location = useLocation();
    const application = useSelector(({ application }) => application);
    const page = useSelector(({ page }) => page);

    if (!page.data || !page.data.seo || !application || !location) {
        return null;
    }

    let canonical = null;
    let title = null;
    let description = null;
    let keywords = null;
    let noIndex = null;
    let noFollow = null;
    let ogTitle = null;
    let ogDescription = null;
    let ogImage = null;
    let ogUrl = null;
    let ogType = "website";
    let robots = null;
    const ogLocale = application?.locale || null;
    const pageData = page.data || {};
    const postType = pageData.post_type || "";
    const postTemplate = pageData.template || "";
    const seoData = pageData.seo || {};
    const query = getQueryParams(location);
    const facebookAppId = process.env.REACT_APP_FACEBOOK_APP_ID;
    const facebookDomainVerification = getCountrySpecificVariable("REACT_APP_FACEBOOK_DOMAIN_VERIFICATION", ogLocale);
    const protocol = application.protocol || "https://";

    const isPaginationPage =
        postType === "storm_category" ||
        (postType === "storm_brand" && postTemplate === "default") ||
        (postType === "page" && postTemplate === "search") ||
        (postType === "article" && postTemplate === "article_collection");

    title = isPresent(seoData.title) ? seoData.title : isPresent(pageData.title) ? pageData.title : null;
    if (postType === "article" && postTemplate === "article_collection" && query.category) {
        const category = decodeURI(query.category);
        title = `${title} | ${category}`;
    }

    //PLP description
    const { settings, paragraph } = pageData.hero_content?.[0]?.data?.data || {};
    const plpDescription =
        postType === "storm_category" && activeSetting(settings, "paragraph") && !!paragraph?.length
            ? paragraph[0]
            : null;

    description = isPresent(seoData.description)
        ? seoData.description
        : isPresent(seoData.og_description)
          ? seoData.og_description
          : isPresent(plpDescription)
            ? plpDescription
            : null;

    canonical = buildCanonicalUrl(page, application, query, isPaginationPage) || null;
    ogUrl = isPresent(canonical) ? canonical : null;

    keywords = isPresent(seoData.keywords) ? seoData.keywords : null;

    ogTitle = isPresent(seoData.og_title) ? seoData.og_title : title;
    ogDescription = isPresent(seoData.og_description) ? subDesc(seoData.og_description) : description;
    const rawOgImageUrl = isPresent(seoData.og_image) ? seoData.og_image : null;

    if (rawOgImageUrl) {
        const strippedImageUrl = rawOgImageUrl.match(/q=[0-9]+&w=[0-9]+&h=[0-9]+&auto=[a-zA-Z]+/g)
            ? rawOgImageUrl.substring(0, rawOgImageUrl.lastIndexOf("?q="))
            : rawOgImageUrl;

        ogImage = imgixSrc(strippedImageUrl, {
            q: 70,
            w: 1200,
            h: 630,
            auto: "format",
        });
    }

    noIndex = isPresent(seoData.no_index) ? "noindex" : null;
    noFollow = isPresent(seoData.no_follow) ? "nofollow" : null;

    if (isPaginationPage) {
        // Insert page number in SEO title
        if (query.page && query.page > 1) {
            title = t("seo.pagination", { page: query.page, title });
        }
    }

    if (page.type === "article") {
        ogType = "article";
    }

    robots = returnRobotString([noIndex, noFollow]);

    const localeAlternates = page.localeAlternates || null;
    let hreflangs;

    if (localeAlternates) {
        const languages = getLanguages(application.languages, ogLocale);
        hreflangs = getHreflangs(localeAlternates, languages);
    }

    const breadcrumbs = [
        {
            "@type": "ListItem",
            position: 1,
            name: t("home"),
            item: protocol + application.origin,
        },
    ];

    const blogPath = application.config?.options?.specified_pages?.magazine;
    if (page.type === "article" && page.template === "default" && blogPath) {
        breadcrumbs.push({
            "@type": "ListItem",
            position: 2,
            name: t("articles.collection_title"),
            item: protocol + application.origin + blogPath,
        });

        const primaryCategory = pageData.primary_category;
        if (primaryCategory?.name) {
            breadcrumbs.push({
                "@type": "ListItem",
                position: 3,
                name: primaryCategory.name,
                item: `${protocol}${application.origin}${blogPath}?category=${encodeURIComponent(
                    primaryCategory.name,
                )}`,
            });
        }
    } else if (page?.data?.breadcrumbs) {
        page.data.breadcrumbs.forEach((item, index) => {
            breadcrumbs.push({
                "@type": "ListItem",
                position: index + 2,
                name: decodeHTMLEntities(item.title),
                item: protocol + application.origin + item.permalink,
            });
        });
    }

    breadcrumbs.push({
        "@type": "ListItem",
        position: breadcrumbs.length + 1,
        name: decodeHTMLEntities(page.data.title),
    });

    const componentProps = {};
    let StructuredDataComponent;

    if (page.data.permalink === "/") {
        StructuredDataComponent = structuredDatas.website;
        componentProps.application = application;
    } else if (page.type !== "article") {
        StructuredDataComponent = structuredDatas.webpage;
        Object.assign(componentProps, { breadcrumbs, name: title, description });
    }

    return (
        <>
            <Helmet>
                {title && <title>{decodeHTMLEntities(title)}</title>}
                {canonical && <link href={canonical} rel="canonical" />}
                {description && <meta name="description" content={decodeHTMLEntities(description)} />}
                {keywords && <meta name="keywords" content={keywords} />}
                {robots && <meta name="robots" content={robots} />}
                {ogTitle && <meta property="og:title" content={ogTitle} />}
                {ogDescription && <meta property="og:description" content={ogDescription} />}
                {ogType && <meta property="og:type" content={ogType} />}
                {ogUrl && <meta property="og:url" content={ogUrl} />}
                {ogImage && <meta property="og:image" content={ogImage} />}
                {ogLocale && <meta property="og:locale" content={ogLocale} />}
                {facebookAppId && <meta property="fb:app_id" content={facebookAppId} />}
                {facebookDomainVerification && (
                    <meta name="facebook-domain-verification" content={facebookDomainVerification} />
                )}
            </Helmet>

            {StructuredDataComponent && <StructuredDataComponent {...componentProps} />}

            {hreflangs && <LanguageLinks links={hreflangs} />}
        </>
    );
};

export default PageMeta;
